import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Download, CurrencyInr, ArrowsClockwise } from '@phosphor-icons/react';
import { toDDMMYYYY } from '../lib/dateUtils';
import { loadCompanies } from '../lib/companies';
import { usePagination } from '../components/TablePagination';
import { Amount } from '../contexts/AmountVisibilityContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Build a list of Indian financial years (Apr 1 → Mar 31). Returns most-recent first.
const buildFYList = (n = 6) => {
  const now = new Date();
  const month = now.getMonth(); // 0-indexed
  const year = now.getFullYear();
  // FY starts in April. Before Apr → previous year.
  const currentFYStart = month >= 3 ? year : year - 1;
  const list = [];
  for (let i = 0; i < n; i += 1) {
    const s = currentFYStart - i;
    list.push({
      label: `FY ${s}-${String(s + 1).slice(2)}`,
      from: `${s}-04-01`,
      to: `${s + 1}-03-31`,
    });
  }
  return list;
};

// Returns the FY (Apr→Mar) that contains a given YYYY-MM-DD date string.
const fyForDate = (iso, fyList) => {
  if (!iso || iso.length < 7) return null;
  const y = parseInt(iso.slice(0, 4), 10);
  const m = parseInt(iso.slice(5, 7), 10);
  const fyStart = m >= 4 ? y : y - 1;
  return fyList.find((f) => f.from.startsWith(String(fyStart))) || null;
};

const fmtINR = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtINRRaw = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Privacy-aware money: respects global eye-toggle in header.
const M = ({ v, className }) => <Amount value={v} format={fmtINRRaw} className={className} />;

export const TDSReportPage = () => {
  const FY_LIST = useMemo(() => buildFYList(6), []);

  const [fyKey, setFyKey] = useState(FY_LIST[0].label);
  const [fromDate, setFromDate] = useState(FY_LIST[0].from);
  const [toDate, setToDate] = useState(FY_LIST[0].to);
  const [companyId, setCompanyId] = useState('');
  const [section, setSection] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState({ rows: [], total_gross: 0, total_tds: 0, total_net: 0, count: 0, by_section: {}, by_company: {}, by_month: {} });
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadCompanies().then(setCompanies).catch(() => {}); }, []);

  // Probe across the last 3 FYs once on mount — auto-pick the FY that actually contains TDS receipts
  // so the user lands on a non-empty report instead of staring at "No deductions".
  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      try {
        const oldestStart = FY_LIST[FY_LIST.length - 1].from;
        const newestEnd = FY_LIST[0].to;
        const { data: r } = await axios.get(`${API_URL}/api/reports/tds`, {
          params: { from_date: oldestStart, to_date: newestEnd },
          withCredentials: true,
        });
        if (cancelled || !r.rows || r.rows.length === 0) return;
        const latest = r.rows[0]?.paid_date;          // rows are already date-desc
        const hit = fyForDate(latest, FY_LIST);
        if (hit && hit.label !== fyKey) {
          setFyKey(hit.label);
          setFromDate(hit.from);
          setToDate(hit.to);
        }
      } catch { /* ignore — keep default FY */ }
    };
    probe();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPickFY = (key) => {
    setFyKey(key);
    if (key === '__custom') return;
    const fy = FY_LIST.find((f) => f.label === key);
    if (fy) { setFromDate(fy.from); setToDate(fy.to); }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = { from_date: fromDate, to_date: toDate };
      if (companyId) params.company_id = companyId;
      if (section) params.section = section;
      const { data: r } = await axios.get(`${API_URL}/api/reports/tds`, { params, withCredentials: true });
      setData(r);
    } catch (err) {
      console.error('Failed to load TDS report', err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchReport(); }, [fromDate, toDate, companyId, section]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Client-side client filter (search by name)
  const filteredRows = useMemo(() => {
    const q = clientFilter.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter((r) =>
      [r.client_name, r.client_pan, r.client_gstin, r.invoice_number].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [data.rows, clientFilter]);

  const { paged, controls, startIdx } = usePagination(filteredRows, { storageKey: 'tds-report-page-size', testIdPrefix: 'tds' });

  const exportCsv = () => {
    const header = ['#', 'Paid Date', 'Invoice Date', 'Invoice #', 'Company', 'Client', 'PAN', 'GSTIN', 'Section', 'Gross', 'TDS %', 'TDS Amount', 'Net Received', 'Paid Via', 'Reference'];
    const lines = [header.join(',')];
    filteredRows.forEach((r, i) => {
      const row = [
        i + 1,
        r.paid_date || '',
        r.invoice_date || '',
        r.invoice_number || '',
        (r.company_short || r.company_name || '').replace(/,/g, ' '),
        (r.client_name || '').replace(/,/g, ' '),
        r.client_pan || '',
        r.client_gstin || '',
        r.tds_section || '',
        r.gross_amount,
        r.tds_percentage,
        r.tds_amount,
        r.net_amount,
        r.paid_via || '',
        (r.paid_reference || '').replace(/,/g, ' '),
      ];
      lines.push(row.join(','));
    });
    // Totals row
    lines.push(['', '', '', '', '', '', '', '', 'TOTAL', data.total_gross, '', data.total_tds, data.total_net, '', ''].join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TDS-Report-${fromDate}-to-${toDate}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 800);
  };

  return (
    <div data-testid="tds-report-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--dx-text)' }}>TDS Deduction Report</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>
            Track TDS deducted by clients across all invoices — ready for Income-Tax return filing.
          </p>
        </div>
        <Button
          onClick={exportCsv}
          disabled={!filteredRows.length}
          className="text-white"
          style={{ background: 'var(--dx-primary, #1D9E75)' }}
          data-testid="tds-export-csv"
        >
          <Download size={16} className="mr-2" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-5 border" style={{ borderColor: 'var(--dx-border, #E4E4E7)' }}>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs">Financial Year</Label>
            <Select value={fyKey} onValueChange={onPickFY}>
              <SelectTrigger data-testid="tds-fy"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FY_LIST.map((f) => <SelectItem key={f.label} value={f.label}>{f.label}</SelectItem>)}
                <SelectItem value="__custom">Custom date range…</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setFyKey('__custom'); }} data-testid="tds-from" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setFyKey('__custom'); }} data-testid="tds-to" />
          </div>
          <div>
            <Label className="text-xs">Company</Label>
            <Select value={companyId || '__all'} onValueChange={(v) => setCompanyId(v === '__all' ? '' : v)}>
              <SelectTrigger data-testid="tds-company"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All Companies</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Section</Label>
            <Select value={section || '__all'} onValueChange={(v) => setSection(v === '__all' ? '' : v)}>
              <SelectTrigger data-testid="tds-section"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All Sections</SelectItem>
                <SelectItem value="194C">194C — Contractor</SelectItem>
                <SelectItem value="194J">194J — Professional</SelectItem>
                <SelectItem value="194H">194H — Commission</SelectItem>
                <SelectItem value="194I">194I — Rent</SelectItem>
                <SelectItem value="194Q">194Q — Goods</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Client / Invoice</Label>
            <Input
              placeholder="Search…"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              data-testid="tds-client-search"
            />
          </div>
        </div>
      </Card>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <Card className="p-4 border" style={{ borderColor: 'var(--dx-border, #E4E4E7)' }}>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Total Gross</div>
          <div className="text-2xl font-bold mt-1" data-testid="tds-total-gross"><M v={data.total_gross} /></div>
          <div className="text-xs text-gray-400 mt-0.5">{data.count} payments</div>
        </Card>
        <Card className="p-4 border bg-amber-50/50" style={{ borderColor: '#fbbf24' }}>
          <div className="text-xs text-amber-700 uppercase tracking-wide font-semibold">Total TDS Deducted</div>
          <div className="text-2xl font-bold text-amber-700 mt-1" data-testid="tds-total-tds"><M v={data.total_tds} /></div>
          <div className="text-xs text-amber-600 mt-0.5">refund/credit in IT return</div>
        </Card>
        <Card className="p-4 border bg-emerald-50/50" style={{ borderColor: '#34d399' }}>
          <div className="text-xs text-emerald-700 uppercase tracking-wide font-semibold">Net Received</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="tds-total-net"><M v={data.total_net} /></div>
          <div className="text-xs text-emerald-600 mt-0.5">actual cash in bank</div>
        </Card>
        <Card className="p-4 border" style={{ borderColor: 'var(--dx-border, #E4E4E7)' }}>
          <div className="text-xs text-gray-500 uppercase tracking-wide">TDS by Section</div>
          <div className="mt-1 space-y-0.5 text-sm">
            {Object.keys(data.by_section).length === 0 ? (
              <div className="text-gray-400 text-xs italic">No deductions</div>
            ) : Object.entries(data.by_section).map(([k, v]) => (
              <div key={k} className="flex justify-between" data-testid={`tds-by-section-${k}`}>
                <span className="text-gray-600">{k}</span>
                <span className="font-semibold"><M v={v} /></span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* By Company breakdown */}
      {Object.keys(data.by_company).length > 1 && (
        <Card className="p-4 mb-5 border" style={{ borderColor: 'var(--dx-border, #E4E4E7)' }}>
          <div className="text-xs font-bold uppercase tracking-wider mb-2 text-gray-500">TDS by Company</div>
          <div className="flex gap-6 flex-wrap">
            {Object.entries(data.by_company).map(([k, v]) => (
              <div key={k} className="text-sm" data-testid={`tds-by-company-${k}`}>
                <span className="text-gray-600">{k}: </span>
                <span className="font-bold text-amber-700"><M v={v} /></span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Table */}
      <Card className="border" style={{ borderColor: 'var(--dx-border, #E4E4E7)' }}>
        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F4F4F5]">
                <TableHead className="text-xs uppercase tracking-wider font-semibold">#</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Paid Date</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Invoice #</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Company</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Client</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Section</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">Gross</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">TDS %</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">TDS ₹</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">Net ₹</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8 text-gray-400"><ArrowsClockwise size={20} className="inline mr-2 animate-spin" />Loading…</TableCell></TableRow>
              ) : paged.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-10 text-[#52525B]">
                  <CurrencyInr size={32} className="mx-auto mb-2 text-gray-300" />
                  No TDS deductions recorded in this period.
                </TableCell></TableRow>
              ) : paged.map((r, idx) => (
                <TableRow key={r.receipt_id} data-testid={`tds-row-${r.receipt_id}`}>
                  <TableCell className="text-gray-400">{startIdx + idx + 1}</TableCell>
                  <TableCell>{toDDMMYYYY(r.paid_date)}</TableCell>
                  <TableCell className="font-medium">{r.invoice_number}</TableCell>
                  <TableCell>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-50 text-blue-700">
                      {r.company_short || '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div>{r.client_name}</div>
                    {r.client_pan && <div className="text-[10px] text-gray-400">PAN {r.client_pan}</div>}
                  </TableCell>
                  <TableCell>
                    {r.tds_section ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">{r.tds_section}</span>
                    ) : <span className="text-gray-300">—</span>}
                  </TableCell>
                  <TableCell className="text-right font-semibold"><M v={r.gross_amount} /></TableCell>
                  <TableCell className="text-right text-xs">{r.tds_percentage}%</TableCell>
                  <TableCell className="text-right font-bold text-amber-700"><M v={r.tds_amount} /></TableCell>
                  <TableCell className="text-right font-semibold text-emerald-700"><M v={r.net_amount} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {controls}
      </Card>
    </div>
  );
};

export default TDSReportPage;
